import React, { useMemo } from 'react';
import { getGTMClientId, AnalyticsPlatformProvider, useUser } from '@skin-seoul/service';
import { usePersonalizationTracker, BridgeApi } from '@skin-seoul/utils';
import { useCookieConsentStatus } from './ShortcodeCookieConsentProvider';

interface Props {
  children: React.ReactNode;
}

export function ShortcodeAnalyticsPlatformProvider({ children }: Props) {
  const consent = useCookieConsentStatus();
  const { user } = useUser();
  const tracker = usePersonalizationTracker();

  // WordPress does full page reloads on navigation (no SPA routing), so
  // window.location.* and document.referrer are always fresh on mount.
  const platformContext = useMemo(() => {
    const webViewConfig = BridgeApi.getAnalyticsConfig();
    const clientId = webViewConfig ? (tracker.getActorId() ?? '') : getGTMClientId();
    const isBrowser = typeof window !== 'undefined';

    return {
      platform: webViewConfig?.platform ?? ('Web' as const),
      clientId,
      userId: user?.userId,
      pagePath: isBrowser ? window.location.href : undefined,
      pageTitle: isBrowser ? document.title : undefined,
      pageReferrer: isBrowser ? document.referrer || undefined : undefined,
      consent: {
        analyticsStorage: consent.hasAnalyticsConsent ? ('granted' as const) : ('denied' as const),
        adStorage: consent.hasAdvertisingConsent ? ('granted' as const) : ('denied' as const),
      },
      osName: webViewConfig?.osName,
      deviceModel: webViewConfig?.deviceModel,
      appVersion: webViewConfig?.appVersion,
      debugMode: process.env.NODE_ENV === 'development',
    };
  }, [consent.hasAnalyticsConsent, consent.hasAdvertisingConsent, user?.userId, tracker]);

  return <AnalyticsPlatformProvider value={platformContext}>{children}</AnalyticsPlatformProvider>;
}
